import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Trophy, Crown, Play, Radio, Loader2, Medal, Plus, LogIn,
  Check, Eye, Gamepad2, Rocket, Mail, Swords, Clock, X, Zap, Heart, Shield, Share2, Link2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useRoom } from "@/context/RoomContext";
import { PageHeader, Spinner, EmptyState } from "@/components/Bits";
import ClanCard from "@/components/ClanCard";
import DynastyCard from "@/components/DynastyCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const medal = (i) => ["🥇", "🥈", "🥉"][i] || `#${i + 1}`;
const MODE_LABEL = { coop: "Co-op", pvp: "PvP", party: "Party", open: "Open" };

export default function Multiplayer() {
  const navigate = useNavigate();
  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="multiplayer-page">
      <PageHeader title="Multiplayer" icon={Users}
        subtitle="Host live rooms, jump in by code, and battle for the top of shared leaderboards." />
      <Tabs defaultValue="rooms">
        <TabsList className="mb-6" data-testid="multiplayer-tabs">
          <TabsTrigger value="rooms" data-testid="tab-rooms"><Radio className="w-4 h-4 mr-1.5" /> Live Rooms</TabsTrigger>
          <TabsTrigger value="ranked" data-testid="tab-ranked"><Swords className="w-4 h-4 mr-1.5" /> Ranked</TabsTrigger>
          <TabsTrigger value="boards" data-testid="tab-boards"><Trophy className="w-4 h-4 mr-1.5" /> Leaderboards</TabsTrigger>
          <TabsTrigger value="coop" data-testid="tab-coop"><Shield className="w-4 h-4 mr-1.5" /> Co-op Glory</TabsTrigger>
        </TabsList>
        <TabsContent value="rooms"><LiveRooms /></TabsContent>
        <TabsContent value="ranked"><RankedLadder /></TabsContent>
        <TabsContent value="boards"><Leaderboards navigate={navigate} /></TabsContent>
        <TabsContent value="coop"><CoopGlory /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ───────────────────────── LIVE ROOMS ───────────────────────── */
function LiveRooms() {
  const { invites, acceptInvite, declineInvite, joinByCode, joinPublic, enterRoom, matchmake } = useRoom();
  const [rooms, setRooms] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);

  const loadRooms = useCallback(async () => {
    try { const { data } = await api.get("/rooms/public"); setRooms(data.rooms); }
    catch { setRooms([]); }
  }, []);

  useEffect(() => {
    loadRooms();
    const t = setInterval(loadRooms, 8000);
    return () => clearInterval(t);
  }, [loadRooms]);

  if (!rooms) return <Spinner />;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/5 p-4 flex items-start gap-3 text-sm">
        <Radio className="w-5 h-5 text-[hsl(var(--primary))] shrink-0 mt-0.5" />
        <div>
          <b>Live rooms</b> let you play together in real time — create a room from any of your games, share the 6-letter code, and friends can hop in instantly. Your lobby stays with you as you move around the app.
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button onClick={() => setQuickOpen(true)} className="rounded-full bg-gradient-to-r from-[#7c3aed] to-[#a855f7]" data-testid="quick-match-btn">
          <Zap className="w-4 h-4" /> Quick Match
        </Button>
        <Button onClick={() => setCreateOpen(true)} variant="outline" className="rounded-full" data-testid="create-room-btn">
          <Plus className="w-4 h-4" /> Create room
        </Button>
        <JoinCodeInline onJoin={joinByCode} />
      </div>

      {invites.length > 0 && (
        <div className="space-y-2" data-testid="invites-list">
          <h3 className="font-semibold text-sm flex items-center gap-1.5"><Mail className="w-4 h-4 text-[hsl(var(--primary))]" /> Room invites</h3>
          {invites.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 text-sm" data-testid={`invite-${inv.id}`}>
              <span className="truncate"><b>{inv.from_name}</b> invited you to <b>{inv.game_cover} {inv.game_title}</b></span>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" className="rounded-full h-8" onClick={() => acceptInvite(inv)} data-testid={`accept-invite-${inv.id}`}><Check className="w-3.5 h-3.5" /> Join</Button>
                <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => declineInvite(inv.id)}>Decline</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><Gamepad2 className="w-5 h-5 text-[hsl(var(--primary))]" /> Public rooms</h2>
        {rooms.length === 0 ? (
          <EmptyState emoji="🎮" title="No live rooms right now" text="Be the first — create a room from one of your games and invite friends to play together." />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="public-rooms-grid">
            {rooms.map((r) => (
              <div key={r.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 card-hover" data-testid={`room-${r.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-3xl">{r.game_cover}</span>
                  <div className="min-w-0">
                    <div className="font-bold truncate">{r.game_title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">Host: {r.host_name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-3 text-xs">
                  <Badge variant="secondary">{MODE_LABEL[r.mode] || r.mode}</Badge>
                  <Badge variant={r.status === "playing" ? "default" : "outline"} className="gap-1">
                    {r.status === "playing" ? <><Play className="w-2.5 h-2.5" /> In game</> : "Lobby"}
                  </Badge>
                  <span className="flex items-center gap-1 text-[hsl(var(--muted-foreground))] ml-auto"><Users className="w-3 h-3" /> {r.players}/{r.max_players}</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-full flex-1" onClick={() => joinPublic(r.id)} data-testid={`join-room-${r.id}`}><LogIn className="w-3.5 h-3.5" /> Join</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => joinPublic(r.id, true)} data-testid={`spectate-room-${r.id}`}><Eye className="w-3.5 h-3.5" /> Watch</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateRoomDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(rm) => { setCreateOpen(false); enterRoom(rm); }} />
      <QuickMatchDialog open={quickOpen} onClose={() => setQuickOpen(false)} matchmake={matchmake} onDone={() => setQuickOpen(false)} />
    </div>
  );
}

function JoinCodeInline({ onJoin }) {
  const [code, setCode] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onJoin(code); setCode(""); }} className="flex gap-2 flex-1 max-w-sm">
      <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Enter room code"
        maxLength={8} className="rounded-full uppercase tracking-widest" data-testid="join-code-input" />
      <Button type="submit" variant="outline" className="rounded-full" data-testid="join-code-btn"><LogIn className="w-4 h-4" /> Join</Button>
    </form>
  );
}

function CreateRoomDialog({ open, onClose, onCreated }) {
  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState("");
  const [mode, setMode] = useState("coop");
  const [visibility, setVisibility] = useState("public");
  const [maxPlayers, setMaxPlayers] = useState("8");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/arcade").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setGames(list);
      if (list.length) setGameId(list[0].id);
    }).catch(() => setGames([]));
  }, [open]);

  const create = async () => {
    if (!gameId) { toast.error("Pick a game to host"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/rooms", { game_id: gameId, mode, visibility, max_players: parseInt(maxPlayers, 10) });
      toast.success("Room created!");
      onCreated(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-room-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5" /> Create a room</DialogTitle>
          <DialogDescription>Host a live session from any published game and invite friends.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <Field label="Game">
            <Select value={gameId} onValueChange={setGameId}>
              <SelectTrigger data-testid="room-game-select"><SelectValue placeholder="Choose a game" /></SelectTrigger>
              <SelectContent>
                {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.cover_emoji} {g.title}</SelectItem>)}
              </SelectContent>
            </Select>
            {games.length === 0 && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">No published games found — publish a game to the Arcade first.</p>}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mode">
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="room-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MODE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Visibility">
              <Select value={visibility} onValueChange={setVisibility}>
                <SelectTrigger data-testid="room-visibility-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="private">Private (code only)</SelectItem>
                  <SelectItem value="invite">Invite only</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Max players">
            <Select value={maxPlayers} onValueChange={setMaxPlayers}>
              <SelectTrigger data-testid="room-max-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2, 4, 8, 16, 32].map((n) => <SelectItem key={n} value={String(n)}>{n} players</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Cancel</Button>
          <Button className="rounded-full" onClick={create} disabled={busy || !gameId} data-testid="confirm-create-room">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const Field = ({ label, children }) => (
  <div><label className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-1.5 block">{label}</label>{children}</div>
);

function QuickMatchDialog({ open, onClose, matchmake, onDone }) {
  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState("");
  const [mode, setMode] = useState("coop");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBusy(false);
    api.get("/arcade").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setGames(list);
      if (list.length) setGameId(list[0].id);
    }).catch(() => setGames([]));
  }, [open]);

  const go = async () => {
    if (!gameId) { toast.error("Pick a game to match into"); return; }
    setBusy(true);
    const res = await matchmake(gameId, mode);
    setBusy(false);
    if (res) {
      toast.success(res.matched ? "Match found — joining a live lobby! 🎮" : "No open lobbies — you're hosting a fresh one. Invite friends! 🚀");
      onDone();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="quick-match-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-[#a855f7]" /> Quick Match</DialogTitle>
          <DialogDescription>Instantly drop into the best open lobby for a game — or host a new one if none are live.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <Field label="Game">
            <Select value={gameId} onValueChange={setGameId}>
              <SelectTrigger data-testid="qm-game-select"><SelectValue placeholder="Choose a game" /></SelectTrigger>
              <SelectContent>
                {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.cover_emoji} {g.title}</SelectItem>)}
              </SelectContent>
            </Select>
            {games.length === 0 && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">No published games found — publish a game to the Arcade first.</p>}
          </Field>
          <Field label="Mode">
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="qm-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(MODE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Cancel</Button>
          <Button className="rounded-full bg-gradient-to-r from-[#7c3aed] to-[#a855f7]" onClick={go} disabled={busy || !gameId} data-testid="confirm-quick-match">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Finding a match…</> : <><Zap className="w-4 h-4" /> Find match</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── LEADERBOARDS ───────────────────────── */
function Leaderboards({ navigate }) {
  const [boards, setBoards] = useState(null);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [topPlayers, setTopPlayers] = useState(null);
  const [tpWindow, setTpWindow] = useState("all");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/multiplayer/leaderboards"); setBoards(data.boards); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBoards([]); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, [load]);

  const loadTop = useCallback(async () => {
    try { const { data } = await api.get(`/arcade/leaderboards/top-players?window=${tpWindow}`); setTopPlayers(data); }
    catch { setTopPlayers({ players: [], my_rank: null }); }
  }, [tpWindow]);
  useEffect(() => { loadTop(); const t = setInterval(loadTop, 12000); return () => clearInterval(t); }, [loadTop]);

  const openBoard = useCallback(async (gid) => {
    setActive(gid); setDetail(null);
    try { const { data } = await api.get(`/multiplayer/leaderboard/${gid}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => {
    if (!active) return;
    const t = setInterval(async () => {
      try { const { data } = await api.get(`/multiplayer/leaderboard/${active}`); setDetail(data); } catch { /* ignore */ }
    }, 6000);
    return () => clearInterval(t);
  }, [active]);

  if (!boards) return <Spinner />;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/5 p-4 flex items-start gap-3 text-sm">
        <Radio className="w-5 h-5 text-[hsl(var(--primary))] shrink-0 mt-0.5" />
        <div><b>Live leaderboards</b> update in real time as players set new high scores. Open any board to see the rankings, your rank, and who's playing.</div>
      </div>

      {topPlayers && (
        <div className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-400/10 to-transparent p-5" data-testid="top-players-panel">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <h2 className="font-display text-xl font-bold flex items-center gap-2"><Crown className="w-5 h-5 text-amber-400" /> Global Top Players</h2>
            <div className="flex items-center gap-2">
              <div className="flex rounded-full border border-[hsl(var(--border))] overflow-hidden text-xs" data-testid="tp-window-toggle">
                {[["all", "All-time"], ["week", "This week"], ["month", "This month"]].map(([v, l]) => (
                  <button key={v} onClick={() => setTpWindow(v)} data-testid={`tp-window-${v}`}
                    className={`px-3 py-1 font-semibold transition-colors ${tpWindow === v ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-[hsl(var(--accent))]"}`}>{l}</button>
                ))}
              </div>
              {topPlayers.my_rank && <Badge variant="secondary" className="gap-1" data-testid="top-players-my-rank">Rank #{topPlayers.my_rank}</Badge>}
            </div>
          </div>
          {(topPlayers.players || []).length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No champions yet — be the first to set a high score across the arcade!</p>
          ) : (
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] divide-y divide-[hsl(var(--border))]" data-testid="top-players-list">
              <div className="flex items-center justify-between px-3 py-2 text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                <span className="pl-7">Player</span>
                <span className="flex gap-4"><span className="w-12 text-right">🥇 Wins</span><span className="w-16 text-right">Points</span><span className="w-14 text-right">Games</span></span>
              </div>
              {topPlayers.players.map((p, i) => (
                <div key={p.user_id || i} className={`flex items-center justify-between px-3 py-2 text-sm ${i < 3 ? "font-semibold" : ""}`} data-testid={`top-player-row-${i}`}>
                  <span className="flex items-center gap-2 truncate"><span className="w-5 text-center">{medal(i)}</span>{p.avatar_emoji} {p.user_name}</span>
                  <span className="flex gap-4 font-mono tabular-nums">
                    <span className="w-12 text-right text-amber-500 font-bold">{p.golds}</span>
                    <span className="w-16 text-right font-bold text-[hsl(var(--primary))]">{p.total_points}</span>
                    <span className="w-14 text-right text-[hsl(var(--muted-foreground))]">{p.games_played}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <h2 className="font-display text-xl font-bold flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-400" /> Live Leaderboards</h2>
      {boards.length === 0 ? (
        <EmptyState emoji="🏆" title="No live boards yet" text="Publish a game to the Arcade and players' high scores will appear here in real time." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="leaderboards-grid">
          {boards.map((b) => (
            <button key={b.game_id} onClick={() => openBoard(b.game_id)} data-testid={`board-${b.game_id}`}
              className="text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 card-hover">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-3xl">{b.cover_emoji}</span>
                <div className="min-w-0">
                  <div className="font-bold truncate">{b.title}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-2">
                    <span className="flex items-center gap-1"><Play className="w-3 h-3" /> {b.plays}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {b.players}</span>
                  </div>
                </div>
              </div>
              {b.top.length === 0 ? (
                <div className="text-xs text-[hsl(var(--muted-foreground))] py-2">No scores yet — be the first!</div>
              ) : (
                <div className="space-y-1.5">
                  {b.top.slice(0, 3).map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5 truncate"><span className="w-5 text-center">{medal(i)}</span> {s.user_name}</span>
                      <span className="font-bold tabular-nums">{s.score}</span>
                    </div>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && (setActive(null), setDetail(null))}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="board-detail">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{detail?.cover_emoji || "🎮"}</span> {detail?.title || "Leaderboard"}
              <Badge variant="secondary" className="ml-1 gap-1 text-[10px]"><Radio className="w-2.5 h-2.5" /> Live</Badge>
            </DialogTitle>
          </DialogHeader>
          {!detail ? (
            <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))]" /></div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                <Stat label="Players" value={detail.total_players} />
                <Stat label="Your best" value={detail.my_best ?? "—"} />
                <Stat label="Your rank" value={detail.my_rank ? `#${detail.my_rank}` : "—"} />
              </div>
              <div>
                <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-amber-400" /> Top players</div>
                {detail.top.length === 0 ? <div className="text-sm text-[hsl(var(--muted-foreground))]">No scores yet.</div> : (
                  <div className="rounded-xl border border-[hsl(var(--border))] divide-y divide-[hsl(var(--border))]" data-testid="board-rankings">
                    {detail.top.map((s, i) => (
                      <div key={i} className={`flex items-center justify-between px-3 py-2 text-sm ${i < 3 ? "font-semibold" : ""}`}>
                        <span className="flex items-center gap-2 truncate"><span className="w-6 text-center">{medal(i)}</span> {s.user_name}</span>
                        <span className="tabular-nums">{s.score}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {detail.recent?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5"><Medal className="w-3.5 h-3.5" /> Recent activity</div>
                  <div className="flex flex-wrap gap-1.5">
                    {detail.recent.map((s, i) => (
                      <Badge key={i} variant="outline" className="text-[11px]">{s.user_name}: {s.score}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <Button onClick={() => navigate(`/app/arcade/${active}`)} className="w-full rounded-full" data-testid="board-play-btn">
                <Play className="w-4 h-4" /> Play & set a high score
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{label}</div>
    <div className="text-xl font-bold tabular-nums">{value}</div>
  </div>
);

/* ───────────────────────── RANKED LADDER ───────────────────────── */
function RankedLadder() {
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [recap, setRecap] = useState(null);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([api.get("/ranked/ladder?limit=100"), api.get("/ranked/season")]);
      setData(l.data); setMe(s.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setData({ ladder: [] }); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  useEffect(() => { api.get("/ranked/recap").then((r) => setRecap(r.data.recap)).catch(() => {}); }, []);

  const dismissRecap = () => {
    if (recap) api.post("/ranked/recap/ack", { season_id: recap.season_id }).catch(() => {});
    setRecap(null);
  };

  if (!data || !me) return <Spinner />;
  const tier = me.me.tier;
  const endsSoon = me.days_left <= 3;

  return (
    <div className="space-y-6" data-testid="ranked-panel">
      {recap && !recap.acked && (
        <div className="relative rounded-2xl border border-amber-500/40 bg-gradient-to-r from-amber-500/15 via-amber-500/5 to-transparent p-5" data-testid="ranked-recap">
          <button onClick={dismissRecap} className="absolute top-3 right-3 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="ranked-recap-dismiss"><X className="w-4 h-4" /></button>
          <div className="flex items-center gap-3">
            <span className="text-4xl">{recap.rank === 1 ? "🥇" : recap.rank === 2 ? "🥈" : recap.rank === 3 ? "🥉" : "🏁"}</span>
            <div>
              <h3 className="font-display text-lg font-bold">{recap.season_name} wrapped — you placed #{recap.rank} of {recap.total_players}!</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                {recap.points} points · {recap.tier.name} tier{recap.reward_credits > 0 ? <> · <b className="text-amber-500">+{recap.reward_credits} bonus credits awarded 🎉</b></> : <> · climb higher this season to make the podium!</>}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className={`rounded-2xl border p-5 ${endsSoon ? "border-red-500/40 bg-gradient-to-br from-red-500/10 to-transparent" : "border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-transparent"}`} data-testid="ranked-season-banner">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-2xl font-extrabold flex items-center gap-2"><Swords className="w-6 h-6 text-violet-400" /> {me.season?.name || "Ranked Season"}</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">{endsSoon ? "⏳ Final days — climb now to lock in your tier and win bonus credits!" : "Win competitive matches (FPS deathmatch & kart races) to earn points and climb the ladder."}</p>
          </div>
          <Badge variant="secondary" className={`gap-1.5 px-3 py-1.5 text-sm ${endsSoon ? "text-red-500" : ""}`} data-testid="ranked-season-countdown">
            <Clock className={`w-4 h-4 ${endsSoon ? "text-red-500" : "text-violet-400"}`} /> {me.days_left > 0 ? `Season ends in ${me.days_left} day${me.days_left === 1 ? "" : "s"}` : `Ends in ${me.hours_left}h`}
          </Badge>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-3" data-testid="ranked-my-stats">
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 sm:col-span-1 text-center">
          <div className="text-4xl mb-1" data-testid="ranked-tier-emoji">{tier.emoji}</div>
          <div className="font-display font-bold" data-testid="ranked-tier-name">{tier.name}</div>
          {tier.next_name && (
            <>
              <div className="mt-2 h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500" style={{ width: `${tier.progress}%` }} />
              </div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{tier.next_at - me.me.points} pts to {tier.next_name}</div>
            </>
          )}
        </div>
        <Stat label="Season points" value={me.me.points} />
        <Stat label="Your rank" value={me.me.rank ? `#${me.me.rank}` : "—"} />
        <Stat label="Wins / Matches" value={`${me.me.wins} / ${me.me.matches}`} />
      </div>

      {me.champions?.length > 0 && (
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent p-5" data-testid="ranked-champions">
          <h3 className="font-display text-lg font-bold flex items-center gap-2 mb-3"><Crown className="w-5 h-5 text-amber-400" /> {me.champions[0].season_name} champions</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            {me.champions.map((c) => (
              <div key={c.user_id} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 flex items-center gap-3" data-testid={`champion-${c.rank}`}>
                <span className="text-2xl">{{ 1: "🥇", 2: "🥈", 3: "🥉" }[c.rank]}</span>
                <div className="min-w-0">
                  <div className="font-bold truncate">{c.avatar} {c.name}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{c.points} pts · +{c.credits} credits</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="font-display text-xl font-bold flex items-center gap-2 mb-3"><Crown className="w-5 h-5 text-amber-400" /> Season ladder</h3>
        {data.ladder.length === 0 ? (
          <EmptyState emoji="⚔️" title="No ranked players yet" text="Be the first to climb — start a PvP deathmatch or kart race from a live room and finish to earn points." />
        ) : (
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] divide-y divide-[hsl(var(--border))]" data-testid="ranked-ladder-list">
            {data.ladder.map((p) => (
              <div key={p.user_id} className={`flex items-center justify-between px-3 py-2.5 text-sm ${p.is_me ? "bg-[hsl(var(--primary))]/10 font-bold" : ""} ${p.rank <= 3 ? "font-semibold" : ""}`} data-testid={`ranked-row-${p.rank}`}>
                <span className="flex items-center gap-2 truncate">
                  <span className="w-7 text-center">{medal(p.rank - 1)}</span>
                  <span title={p.tier.name}>{p.tier.emoji}</span>
                  <span>{p.avatar}</span>
                  <span className="truncate">{p.name}{p.is_me && " (you)"}</span>
                </span>
                <span className="flex items-center gap-4 font-mono tabular-nums">
                  <span className="w-14 text-right text-amber-500">{p.wins}W</span>
                  <span className="w-20 text-right font-bold text-[hsl(var(--primary))]">{p.points} pts</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* ───────────────────────── CO-OP GLORY (weekly squad + global boards) ───────────────────────── */
const COOP_BOARDS = [
  { key: "rescues", label: "Top Medics", icon: Heart, unit: "revives", stat: "rescues" },
  { key: "kills", label: "Top Slayers", icon: Swords, unit: "kills", stat: "kills" },
  { key: "mvp", label: "Boss MVPs", icon: Crown, unit: "MVP wins", stat: "mvp" },
];
const CLAN_BOARDS = [
  { key: "score", label: "Clan Power", icon: Shield, unit: "clan power", stat: "score" },
  { key: "rescues", label: "Top Medics", icon: Heart, unit: "revives", stat: "rescues" },
  { key: "kills", label: "Top Slayers", icon: Swords, unit: "kills", stat: "kills" },
  { key: "mvp", label: "Boss MVPs", icon: Crown, unit: "MVP wins", stat: "mvp" },
];

function CoopGlory() {
  const [weekly, setWeekly] = useState(undefined);
  const [board, setBoard] = useState(null);
  const [sort, setSort] = useState("rescues");
  const [clans, setClans] = useState(undefined);
  const [clanSort, setClanSort] = useState("score");
  const [clanPeriod, setClanPeriod] = useState("alltime");
  const [clanOfWeek, setClanOfWeek] = useState(undefined);
  const [archive, setArchive] = useState(undefined);
  const [archiveWeek, setArchiveWeek] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [archiveView, setArchiveView] = useState("weeks");
  const [allTime, setAllTime] = useState(undefined);
  const [archiveQ, setArchiveQ] = useState("");
  const [dynasty, setDynasty] = useState(undefined);
  const [rivalry, setRivalry] = useState(null);
  const [clanCard, setClanCard] = useState(null);
  const [dynastyCard, setDynastyCard] = useState(null);
  const [trophyDlg, setTrophyDlg] = useState(null);

  useEffect(() => {
    api.get("/rooms/coop/weekly-squad").then((r) => setWeekly(r.data)).catch(() => setWeekly({ top: null }));
    api.get("/guilds/coop-leaderboard-weekly", { params: { period: "last" } }).then((r) => setClanOfWeek(r.data.champion)).catch(() => setClanOfWeek(null));
    api.get("/guilds/clan-trophies").then((r) => { setDynasty(r.data.dynasty || []); setRivalry(r.data.rivalry || null); }).catch(() => { setDynasty([]); setRivalry(null); });
  }, []);
  useEffect(() => {
    if (archiveView !== "weeks") return;
    setArchive(undefined);
    const t = setTimeout(() => {
      api.get("/rooms/coop/season-archive", { params: { limit: 8, q: archiveQ || undefined } }).then((r) => setArchive(r.data.weeks || [])).catch(() => setArchive([]));
    }, archiveQ ? 350 : 0);
    return () => clearTimeout(t);
  }, [archiveView, archiveQ]);
  useEffect(() => {
    if (archiveView !== "alltime") return;
    setAllTime(undefined);
    const t = setTimeout(() => {
      api.get("/rooms/coop/all-time-squads", { params: { limit: 12, q: archiveQ || undefined } }).then((r) => setAllTime(r.data.squads || [])).catch(() => setAllTime([]));
    }, archiveQ ? 350 : 0);
    return () => clearTimeout(t);
  }, [archiveView, archiveQ]);
  useEffect(() => {
    setClans(undefined);
    const url = clanPeriod === "weekly" ? "/guilds/coop-leaderboard-weekly" : "/guilds/coop-leaderboard";
    const params = clanPeriod === "weekly" ? { period: "current", sort: clanSort } : { sort: clanSort };
    api.get(url, { params }).then((r) => setClans(r.data.guilds || [])).catch(() => setClans([]));
  }, [clanSort, clanPeriod]);
  useEffect(() => {
    setBoard(null);
    api.get(`/rooms/coop/leaderboard`, { params: { sort, limit: 25 } }).then((r) => setBoard(r.data.leaders || [])).catch(() => setBoard([]));
  }, [sort]);
  useEffect(() => {
    if (!archiveWeek) { setWeekData(null); return; }
    setWeekData(null);
    api.get(`/rooms/coop/season-archive/${archiveWeek}`).then((r) => setWeekData(r.data)).catch(() => setWeekData({ squads: [] }));
  }, [archiveWeek]);

  const active = COOP_BOARDS.find((b) => b.key === sort) || COOP_BOARDS[0];
  const activeClan = CLAN_BOARDS.find((b) => b.key === clanSort) || CLAN_BOARDS[0];
  const top = weekly?.top;
  const weekLabel = (w) => { const m = /^(\d{4})-W(\d{2})$/.exec(w || ""); return m ? `${m[1]} · Week ${parseInt(m[2], 10)}` : w; };
  const trophyInfo = Object.fromEntries((dynasty || []).map((d) => [d.id, d]));
  const inviteClan = (c) => {
    const link = `${window.location.origin}/app/guilds?g=${c.id}`;
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(link).then(() => toast.success(`Invite link copied — recruit for ${c.name}!`)).catch(() => toast.error("Couldn't copy the link."));
    else toast.error("Clipboard not available.");
  };

  return (
    <div className="space-y-6" data-testid="coop-glory">
      {/* Weekly Squad spotlight */}
      <div className="rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-fuchsia-500/10 p-5" data-testid="weekly-squad-spotlight">
        <div className="flex items-center gap-2 text-sm font-bold text-amber-500 mb-3"><Crown className="w-5 h-5" /> Squad of the Week</div>
        {weekly === undefined ? (
          <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading this week's champions…</div>
        ) : top && top.members?.length ? (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex -space-x-2">
              {top.members.slice(0, 5).map((m, i) => (
                <div key={i} className="w-11 h-11 rounded-full bg-[hsl(var(--card))] border-2 border-amber-400 grid place-items-center text-xl shadow" title={m.name}>{m.avatar || "🎮"}</div>
              ))}
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold truncate">{top.members.map((m) => m.name).join(" · ")}</div>
              <div className="text-sm text-[hsl(var(--muted-foreground))]">👑 {top.bosses} boss{top.bosses === 1 ? "" : "es"} felled together · {top.runs} run{top.runs === 1 ? "" : "s"} this week</div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-[hsl(var(--muted-foreground))]">No squad has felled a boss together yet this week. Team up in a co-op room and claim the crown! 👑</div>
        )}
      </div>

      {/* Season Archive — Hall of Fame of past weekly champions + all-time greats */}
      <div data-testid="coop-season-archive">
        <div className="flex items-center gap-2 text-sm font-bold mb-3"><Trophy className="w-5 h-5 text-amber-400" /> Hall of Fame
          <span className="ml-auto flex gap-1.5">
            <Button size="sm" variant={archiveView === "weeks" ? "default" : "outline"} className="rounded-full h-7 px-3 text-xs" onClick={() => setArchiveView("weeks")} data-testid="archive-view-weeks">By week</Button>
            <Button size="sm" variant={archiveView === "alltime" ? "default" : "outline"} className="rounded-full h-7 px-3 text-xs" onClick={() => setArchiveView("alltime")} data-testid="archive-view-alltime">All-time</Button>
          </span>
        </div>
        <input value={archiveQ} onChange={(e) => setArchiveQ(e.target.value)} placeholder="Search the Hall of Fame by teammate name…" data-testid="archive-search-input"
          className="w-full rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40" />
        {archiveView === "alltime" ? (
          allTime === undefined ? (
            <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Summoning the all-time greats…</div>
          ) : allTime.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-4 text-sm text-[hsl(var(--muted-foreground))]">{archiveQ ? `No squads found matching “${archiveQ}”. Try another teammate's name.` : "No legendary squads yet — be the first crew to fell a boss together! 🐉"}</div>
          ) : (
            <div className="space-y-2" data-testid="coop-alltime-list">
              {allTime.map((s, i) => {
                const members = s.members || [];
                return (
                  <div key={i} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${i === 0 ? "border-amber-400/50 bg-amber-400/10" : "border-[hsl(var(--border))] bg-[hsl(var(--card))]"}`} data-testid={`coop-alltime-${i}`}>
                    <span className="w-8 text-center font-bold tabular-nums text-[hsl(var(--muted-foreground))]">{medal(i)}</span>
                    <span className="flex -space-x-2 shrink-0">
                      {members.slice(0, 4).map((m, k) => (
                        <span key={k} className="w-8 h-8 rounded-full bg-[hsl(var(--muted))] border-2 border-[hsl(var(--card))] grid place-items-center text-sm" title={m.name}>{m.avatar || "🎮"}</span>
                      ))}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="font-semibold text-sm truncate block">{members.map((m) => m.name).join(" · ") || "A legendary squad"}</span>
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">{s.runs} run{s.runs === 1 ? "" : "s"} · across {s.weeks} week{s.weeks === 1 ? "" : "s"}</span>
                    </span>
                    <span className="font-display font-bold tabular-nums text-amber-400">👑 {s.bosses}<span className="text-[10px] text-[hsl(var(--muted-foreground))] ml-1 font-normal">bosses</span></span>
                  </div>
                );
              })}
            </div>
          )
        ) : archive === undefined ? (
          <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading champions…</div>
        ) : archive.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-4 text-sm text-[hsl(var(--muted-foreground))]">{archiveQ ? `No champion weeks match “${archiveQ}”. Try another teammate's name.` : "No past champions yet — this week's winning squad becomes the first entry in the hall of fame. 🏆"}</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="coop-archive-list">
            {archive.map((w, i) => {
              const c = w.champ || {}; const members = c.members || [];
              return (
                <button key={w.week} onClick={() => setArchiveWeek(w.week)} className="text-left rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3.5 hover:border-amber-400/60 hover:bg-amber-400/5 transition-colors" data-testid={`coop-archive-${i}`}>
                  <div className="text-xs font-semibold text-amber-500 mb-2 flex items-center gap-1.5"><Crown className="w-3.5 h-3.5" /> {weekLabel(w.week)}<span className="ml-auto text-[hsl(var(--muted-foreground))] font-normal">View standings →</span></div>
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-2">
                      {members.slice(0, 4).map((m, k) => (
                        <div key={k} className="w-9 h-9 rounded-full bg-[hsl(var(--muted))] border-2 border-[hsl(var(--card))] grid place-items-center text-base" title={m.name}>{m.avatar || "🎮"}</div>
                      ))}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{members.map((m) => m.name).join(" · ") || "A legendary squad"}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))]">👑 {c.bosses || 0} boss{(c.bosses || 0) === 1 ? "" : "es"} felled · {c.runs || 0} run{(c.runs || 0) === 1 ? "" : "s"}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Clan Rankings — guild co-op board */}
      <div data-testid="coop-clan-board">
        <div className="flex items-center gap-2 text-sm font-bold mb-3"><Shield className="w-5 h-5 text-sky-400" /> Clan Rankings — crews compete as teams</div>
        {clanOfWeek && (
          <div className="rounded-xl border border-amber-400/50 bg-gradient-to-r from-amber-400/15 to-transparent p-3 mb-3 flex items-center gap-3" data-testid="coop-clan-of-week">
            <span className="text-2xl">👑</span>
            <span className="min-w-0 flex-1">
              <span className="text-[11px] font-bold uppercase tracking-wide text-amber-500 block">Clan of the Week</span>
              <span className="font-semibold truncate block">{clanOfWeek.emoji} {clanOfWeek.name}{clanOfWeek.tag && <span className="text-[10px] font-mono ml-1 text-[hsl(var(--muted-foreground))]">[{clanOfWeek.tag}]</span>}</span>
            </span>
            <span className="font-display font-bold tabular-nums text-amber-400 shrink-0">{clanOfWeek.score}<span className="text-[10px] text-[hsl(var(--muted-foreground))] ml-1 font-normal">power</span></span>
          </div>
        )}
        <div className="flex items-center gap-1.5 mb-3" data-testid="coop-clan-period">
          <Button size="sm" variant={clanPeriod === "alltime" ? "default" : "outline"} className="rounded-full h-7 px-3 text-xs" onClick={() => setClanPeriod("alltime")} data-testid="clan-period-alltime">All-time</Button>
          <Button size="sm" variant={clanPeriod === "weekly" ? "default" : "outline"} className="rounded-full h-7 px-3 text-xs" onClick={() => setClanPeriod("weekly")} data-testid="clan-period-weekly">This week</Button>
        </div>
        <div className="flex flex-wrap gap-2 mb-3" data-testid="coop-clan-tabs">
          {CLAN_BOARDS.map((b) => {
            const Icon = b.icon;
            return (
              <Button key={b.key} size="sm" variant={clanSort === b.key ? "default" : "outline"} className="rounded-full gap-1.5" onClick={() => setClanSort(b.key)} data-testid={`coop-clan-tab-${b.key}`}>
                <Icon className="w-4 h-4" /> {b.label}
              </Button>
            );
          })}
        </div>
        {clans === undefined ? (
          <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Tallying clan glory…</div>
        ) : clans.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[hsl(var(--border))] p-4 text-sm text-[hsl(var(--muted-foreground))]">{clanPeriod === "weekly" ? "No clan has racked up co-op glory yet this week — the board resets every week, so jump in and claim the fresh top spot! 🛡️" : "No clans on the board yet. Join or start a guild, then rack up co-op revives, kills and boss kills together to climb the ranks! 🛡️"}</div>
        ) : (
          <div className="space-y-2" data-testid="coop-clan-list">
            {clans.map((c, i) => (
              <div key={c.id} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${c.is_member ? "border-sky-500/50 bg-sky-500/10" : "border-[hsl(var(--border))] bg-[hsl(var(--card))]"}`} data-testid={`coop-clan-${i}`}>
                <span className="w-8 text-center font-bold tabular-nums text-[hsl(var(--muted-foreground))]">{medal(i)}</span>
                <span className="w-9 h-9 rounded-lg grid place-items-center text-lg shrink-0" style={{ background: `${c.banner || "#1f6feb"}22` }}>{c.emoji || "🛡️"}</span>
                <span className="flex-1 min-w-0">
                  <span className="font-semibold truncate flex items-center gap-1.5">{c.name}{c.tag && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">{c.tag}</span>}{c.trophies > 0 && <button onClick={() => setTrophyDlg({ name: c.name, emoji: c.emoji, weeks: (trophyInfo[c.id]?.weeks) || [] })} className="text-[10px] text-amber-400 font-bold hover:underline" data-testid={`coop-clan-trophies-${i}`} title="See weeks reigned as Clan of the Week">🏆 {c.trophies}</button>}{c.is_member && <span className="text-[10px] text-sky-500 font-bold">· your clan</span>}</span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">🩹 {c.rescues} · 💥 {c.kills} · 👑 {c.boss_kills} · 🏆 {c.mvp} · {c.contributors}/{c.member_count} playing</span>
                </span>
                <span className="font-display font-bold tabular-nums text-sky-400">{c[activeClan.stat]}<span className="text-[10px] text-[hsl(var(--muted-foreground))] ml-1 font-normal">{activeClan.unit}</span></span>
                {(i === 0 || ["owner", "officer"].includes(c.my_role)) && (
                  <span className="flex items-center gap-0.5 shrink-0">
                    {i === 0 && (
                      <button onClick={() => setClanCard(c)} title="Share this clan" data-testid={`coop-clan-brag-${i}`} className="p-1.5 rounded-full hover:bg-[hsl(var(--accent))] text-amber-400 transition-colors"><Share2 className="w-4 h-4" /></button>
                    )}
                    {["owner", "officer"].includes(c.my_role) && (
                      <button onClick={() => inviteClan(c)} title="Copy invite link" data-testid={`coop-clan-invite-${i}`} className="p-1.5 rounded-full hover:bg-[hsl(var(--accent))] text-sky-400 transition-colors"><Link2 className="w-4 h-4" /></button>
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {dynasty && dynasty.length > 0 && (
          <div className="mt-3 rounded-xl border border-amber-400/40 bg-[hsl(var(--card))] p-3" data-testid="coop-dynasty">
            <div className="text-xs font-bold text-amber-500 mb-2 flex items-center gap-1.5">
              <Trophy className="w-3.5 h-3.5" /> Trophy Cabinet — Clan of the Week dynasty
              <button onClick={() => setDynastyCard(dynasty[0])} className="ml-auto p-1 rounded-full hover:bg-[hsl(var(--accent))] text-amber-400 transition-colors" title="Share the reigning dynasty" data-testid="coop-dynasty-share"><Share2 className="w-4 h-4" /></button>
            </div>
            {rivalry && rivalry.challenger && (
              <div className="text-[11px] mb-2 flex items-center gap-1 flex-wrap text-[hsl(var(--muted-foreground))]" data-testid="coop-dynasty-rivalry">
                <span className="font-bold text-amber-400">{rivalry.leader.emoji} {rivalry.leader.name}</span>
                <span>leads ·</span>
                <span className="font-semibold text-[hsl(var(--foreground))]">{rivalry.challenger.emoji} {rivalry.challenger.name}</span>
                <span>{rivalry.gap <= 0 ? "is tied 🔥 for the crown" : `is ${rivalry.gap} 🏆 behind — chasing the crown`}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {dynasty.slice(0, 6).map((d, i) => (
                <button key={d.id} onClick={() => setTrophyDlg({ name: d.name, emoji: d.emoji, weeks: d.weeks || [] })} className="flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 px-2.5 py-1 text-xs hover:border-amber-400/60 transition-colors" data-testid={`coop-dynasty-${i}`}>
                  <span className="text-sm">{d.emoji || "🛡️"}</span>
                  <span className="font-semibold truncate max-w-[120px]">{d.name}</span>
                  <span className="text-amber-400 font-bold">🏆 {d.trophies}</span>
                  {d.streak >= 2 && <span className="text-[10px] font-bold text-orange-400 flex items-center gap-0.5" title={`${d.streak} weeks in a row${d.best_streak > d.streak ? ` · best streak ${d.best_streak}` : ""}`} data-testid={`coop-dynasty-streak-${i}`}>🔥{d.streak}</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2" data-testid="coop-board-tabs">
        {COOP_BOARDS.map((b) => {
          const Icon = b.icon;
          return (
            <Button key={b.key} size="sm" variant={sort === b.key ? "default" : "outline"} className="rounded-full gap-1.5" onClick={() => setSort(b.key)} data-testid={`coop-board-${b.key}`}>
              <Icon className="w-4 h-4" /> {b.label}
            </Button>
          );
        })}
      </div>

      {board === null ? (
        <Spinner />
      ) : board.length === 0 ? (
        <EmptyState icon={active.icon} title="No champions yet" description="Play co-op adventures — revive teammates, slay foes and fell bosses — to climb this board." />
      ) : (
        <div className="space-y-2" data-testid="coop-leaderboard-list">
          {board.map((u, i) => (
            <div key={u.id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5" data-testid={`coop-leader-${i}`}>
              <span className="w-8 text-center font-bold tabular-nums text-[hsl(var(--muted-foreground))]">{medal(i)}</span>
              <span className="text-2xl">{u.avatar}</span>
              <span className="flex-1 min-w-0">
                <span className="font-semibold truncate flex items-center gap-1.5">{u.name}{u.medic && <span title="Medic" className="text-emerald-500">🩹</span>}</span>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">🩹 {u.rescues} · 💥 {u.kills} · 👑 {u.mvp}</span>
              </span>
              <span className="font-display font-bold tabular-nums text-[hsl(var(--primary))]">{u[active.stat]}<span className="text-[10px] text-[hsl(var(--muted-foreground))] ml-1 font-normal">{active.unit}</span></span>
            </div>
          ))}
        </div>
      )}

      {/* Season Archive drill-down — full standings for one past week */}
      <Dialog open={!!archiveWeek} onOpenChange={(v) => { if (!v) setArchiveWeek(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="coop-archive-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Crown className="w-5 h-5 text-amber-400" /> {weekLabel(archiveWeek)} — full standings</DialogTitle>
            <DialogDescription>Every squad that felled a boss together that week, ranked by bosses down.</DialogDescription>
          </DialogHeader>
          {weekData === null ? (
            <div className="py-6"><Spinner /></div>
          ) : (weekData.squads || []).length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))] py-4">No squad records for this week.</p>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="coop-archive-standings">
              {weekData.squads.map((s, i) => (
                <div key={i} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${i === 0 ? "border-amber-400/50 bg-amber-400/10" : "border-[hsl(var(--border))]"}`} data-testid={`coop-archive-standing-${i}`}>
                  <span className="w-8 text-center font-bold tabular-nums text-[hsl(var(--muted-foreground))]">{medal(i)}</span>
                  <span className="flex -space-x-2 shrink-0">
                    {(s.members || []).slice(0, 4).map((m, k) => (
                      <span key={k} className="w-8 h-8 rounded-full bg-[hsl(var(--muted))] border-2 border-[hsl(var(--card))] grid place-items-center text-sm" title={m.name}>{m.avatar || "🎮"}</span>
                    ))}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="font-semibold text-sm truncate block">{(s.members || []).map((m) => m.name).join(" · ") || "A squad"}</span>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{s.runs} run{s.runs === 1 ? "" : "s"}</span>
                  </span>
                  <span className="font-display font-bold tabular-nums text-amber-400">👑 {s.bosses}<span className="text-[10px] text-[hsl(var(--muted-foreground))] ml-1 font-normal">bosses</span></span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ClanCard clan={clanCard} open={!!clanCard} onOpenChange={(v) => { if (!v) setClanCard(null); }} />
      <DynastyCard clan={dynastyCard} open={!!dynastyCard} onOpenChange={(v) => { if (!v) setDynastyCard(null); }} />

      <Dialog open={!!trophyDlg} onOpenChange={(v) => { if (!v) setTrophyDlg(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="coop-trophy-weeks-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-400" /> {trophyDlg?.emoji} {trophyDlg?.name}</DialogTitle>
            <DialogDescription>Every week this clan reigned as Clan of the Week.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="coop-trophy-weeks-list">
            {(trophyDlg?.weeks || []).length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">No reigns recorded yet.</p>
            ) : (
              (trophyDlg?.weeks || []).slice().sort().reverse().map((w, i) => (
                <div key={w} className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-sm" data-testid={`coop-trophy-week-${i}`}>
                  <span className="text-amber-400">👑</span> <span className="font-semibold">{weekLabel(w)}</span>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
